// src/oura/components/docs/threads/ThreadSheet.tsx
//
// One comment thread of a doc, a view inside the doc drawer (Docs phase 2,
// lane 4: 2.10 to 2.12), the way the Questions view is: "‹ {doc title}" in
// Row A takes staff back to the doc.
//
//   Header   "Question 2" (or Raised by the client, Our note), the thread's
//            state once, its code chip.
//   Body     the opening comment, with the amber flag's sentence when it was
//            resolved without the client's agreement; every reply in order,
//            with who said it and how (in Oura, by email, on WhatsApp, on a
//            call, logged by whom); Edit and Delete only on posts Oura wrote
//            as oura@; the reply box; "Log an answer" for a question.
//   Foot     Send reply when one is written, else Resolve (blue when the
//            client agreed). Resolving without their agreement takes a second
//            click: "The client has not agreed. Resolve anyway?". A resolved
//            thread offers Reopen, which asks why and posts the reason in the
//            thread. After a reply or a resolve the foot offers the next
//            thread that needs us.
//
// Every write is posted by oura@ for the named person who clicked: a reply is
// signed with their name, a logged answer names who logged it. Nothing here
// writes on its own. Rendered only for a doc `canWrite` allows.

import { useState, type ReactNode } from "react";
import { CheckCircle2Icon, RotateCcwIcon } from "lucide-react";
import {
    deleteOwnPost,
    editOwnPost,
    logAnswer,
    reopenThread,
    replyInThread,
    resolveThread,
    type AnswerVia,
    type LogAnswerIn,
} from "@service/aeo/clientDocsWrite";
import { docsFailure, type DocPost, type DocsResult, type DocumentDetail, type StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { joinDotted } from "../../../lib/oura";
import {
    Dot,
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    DrawerPulse,
    DrawerQuote,
    FamilyChip,
    PillButton,
    SURFACE,
    Seg,
    TYPE,
    type DrawerConfirm,
    type DrawerDone,
    type DrawerFootMessage,
} from "@oura/ui";
import { codeFamily, docDate, docsFailureText, ouraAccountOnDoc, trimText } from "@oura/docs";
import { inputClass, textareaClass } from "../../../onboarding/messageText";
import type { DocWriteView } from "../write/model";
import { useSignedInName } from "@oura/docs/signedIn";
import {
    VIA_WORDS,
    authorWord,
    clientAgreed,
    isOurOpening,
    isOurPost,
    lastClientWord,
    needsUsThreads,
    openingPostId,
    postWords,
    threadStatus,
    threadTitle,
    threadView,
    withoutCode,
} from "./model";

type Busy = "reply" | "resolve" | "reopen" | "log" | "edit" | "delete" | null;
type Ask = { kind: "resolve" } | { kind: "delete"; postId: string; whole: boolean } | null;
type LogAnswerWord = "agree" | "change" | "other";

const VIA_ITEMS: Array<{ key: Exclude<AnswerVia, "oura">; label: string }> = [
    { key: "email", label: "Email" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "call", label: "Call" },
];
const ANSWER_ITEMS: Array<{ key: LogAnswerWord; label: string }> = [
    { key: "agree", label: "Agrees" },
    { key: "change", label: "Wants a change" },
    { key: "other", label: "Something else" },
];

/** Malaysian time now, as a datetime-local value ("2026-10-02T10:42"): the business's clock, the one the summary uses, wherever the browser is. */
function localNow(): string {
    return new Date(Date.now() + 8 * 3_600_000).toISOString().slice(0, 16);
}

/** The client's name on the doc: the first person outside ourteam it is shared with. */
function clientNameOf(doc: StaffDocument): string | null {
    return doc.sharing.sharedWith.find((p) => !p.ours && p.role !== "owner")?.name ?? null;
}

/** A resolve or reopen, as one line between the replies. */
function EventLine({ p }: { p: DocPost }) {
    const resolve = p.action === "resolve";
    const Icon = resolve ? CheckCircle2Icon : RotateCcwIcon;
    const who = p.author.onBehalfOf ?? p.signedBy ?? authorWord(p.author);
    const where = p.author.onBehalfOf ? VIA_WORDS[p.via ?? "oura"] : p.where === "oura" ? "in Oura" : "in the doc";
    return (
        <p className={cn(TYPE.tc, "m-0 flex items-start gap-1.5 text-gray-500")}>
            <Icon aria-hidden size={14} className={cn("mt-0.5 shrink-0", resolve ? "text-emerald-600" : "text-blue-600")} />
            <span className="min-w-0 break-words">
                {joinDotted(`${resolve ? "Resolved" : "Reopened"} by ${who} ${where}`, docDate(p.at))}
                {p.text.trim() && <span className="text-gray-700">: &ldquo;{p.text.trim()}&rdquo;</span>}
            </span>
        </p>
    );
}

/** Edit in place: the post's words in a box, Save and Cancel under it. */
function EditBox({ text, busy, onSave, onCancel }: { text: string; busy: boolean; onSave: (text: string) => void; onCancel: () => void }) {
    const [value, setValue] = useState(text);
    const [tried, setTried] = useState(false);
    const error = tried && !value.trim() ? "Write the post first." : null;
    return (
        <div className={cn(SURFACE.inner, "space-y-2")}>
            <textarea
                className={textareaClass(error)}
                rows={3}
                value={value}
                aria-label="Edit the post"
                aria-invalid={Boolean(error)}
                disabled={busy}
                onChange={(e) => setValue(e.target.value)}
            />
            {error && <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>{error}</p>}
            <div className="flex flex-wrap items-center gap-2">
                <PillButton
                    size="sm"
                    variant="primary"
                    busy={busy}
                    onClick={() => {
                        setTried(true);
                        if (value.trim()) onSave(value.trim());
                    }}
                >
                    Save in the doc
                </PillButton>
                <PillButton size="sm" disabled={busy} onClick={onCancel}>
                    Cancel
                </PillButton>
            </div>
        </div>
    );
}

/** Edit and Delete under a post Oura wrote. */
function OwnPostActions({ disabled, onEdit, onDelete, editLabel = "Edit" }: { disabled: boolean; onEdit: () => void; onDelete?: () => void; editLabel?: string }) {
    return (
        <div className="mt-1 flex flex-wrap items-center gap-x-4">
            <PillButton variant="text" disabled={disabled} onClick={onEdit}>
                {editLabel}
            </PillButton>
            {onDelete && (
                <PillButton variant="text" disabled={disabled} onClick={onDelete}>
                    Delete
                </PillButton>
            )}
        </div>
    );
}

export function ThreadSheet({
    threadId,
    doc,
    detail,
    loading,
    onBusy,
    onView,
}: {
    threadId: string;
    doc: StaffDocument;
    detail: DocumentDetail | null;
    loading: boolean;
    onBusy: (busy: boolean) => void;
    /** Another view of the doc: the next thread, the Questions editor, or "doc" to go back. */
    onView: (view: DocWriteView | "doc") => void;
}) {
    const me = useSignedInName();
    const signed = me ? `signed ${me}` : "signed with your name";
    const [reply, setReply] = useState("");
    /** Reopen asks why first: null until Reopen is clicked. */
    const [reopenWhy, setReopenWhy] = useState<string | null>(null);
    const [busy, setBusyState] = useState<Busy>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [said, setSaid] = useState<string | null>(null);
    const [ask, setAsk] = useState<Ask>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [logVia, setLogVia] = useState<Exclude<AnswerVia, "oura">>("whatsapp");
    const [logText, setLogText] = useState("");
    const [logWhen, setLogWhen] = useState(localNow);
    const [logWord, setLogWord] = useState<LogAnswerWord>("other");
    const [logTried, setLogTried] = useState(false);

    const threads = detail?.threads ?? [];
    const t = threads.find((x) => x.id === threadId) ?? null;
    const back = (
        <PillButton disabled={busy !== null} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );

    if (!t) {
        return (
            <>
                <DrawerHead title="Comment" />
                <DrawerBlock>
                    {!detail && loading ? <DrawerPulse lines={3} label="Loading the comment" /> : <p className="m-0 text-gray-700">This comment is no longer in the doc.</p>}
                </DrawerBlock>
                <DrawerFoot second={back} />
            </>
        );
    }

    const client = clientNameOf(doc) ?? "The client";
    const agreed = clientAgreed(t);
    const question = t.kind === "question";
    const canLog = question && !t.resolved && doc.sharing.clientCanSee;

    const run = async <T,>(what: Busy, call: () => Promise<DocsResult<T>>): Promise<DocsResult<T>> => {
        setBusyState(what);
        onBusy(true);
        setMessage(null);
        setSaid(null);
        // The sheet and its drawer always come out of busy: a call that throws
        // reads as a failed one, so its caller shows the plain error line.
        try {
            return await call();
        } catch {
            return docsFailure("network");
        } finally {
            setBusyState(null);
            onBusy(false);
        }
    };

    /* Reply, resolve, reopen */
    const sendReply = async () => {
        if (!reply.trim()) return;
        const r = await run("reply", () => replyInThread(doc.id, t.id, reply.trim()));
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the doc") });
        setReply("");
        setSaid(ouraAccountOnDoc(doc) ? `Posted in the doc from ${ouraAccountOnDoc(doc)}, ${signed}` : `Posted in the doc as Oura, ${signed}`);
    };
    const resolve = async (anyway: boolean) => {
        const r = await run("resolve", () => resolveThread(doc.id, t.id, anyway ? { confirmNotAgreed: true } : {}));
        if (!r.ok) {
            // Their last word is not an agreement: ask once more, in the foot.
            if (r.code === "client_not_agreed" && !anyway) return setAsk({ kind: "resolve" });
            setAsk(null);
            return setMessage({ tone: "error", text: docsFailureText(r, "the doc") });
        }
        setAsk(null);
        setSaid(anyway ? `Resolved ${threadTitle(t, threads).toLowerCase()} without the client's agreement` : `Resolved ${threadTitle(t, threads).toLowerCase()}`);
    };
    const reopen = async () => {
        if (reopenWhy === null) return setReopenWhy("");
        const why = reopenWhy.trim();
        if (!why) return setMessage({ tone: "error", text: "Say why it is reopened first." });
        const r = await run("reopen", () => reopenThread(doc.id, t.id, why));
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the doc") });
        setReopenWhy(null);
        setMessage({ tone: "success", text: "Reopened in the doc, with your reason." });
    };

    /* Our own posts */
    const saveEdit = async (postId: string, text: string) => {
        const r = await run("edit", () => editOwnPost(doc.id, t.id, postId, text));
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the post") });
        setEditing(null);
        setMessage({ tone: "success", text: "Edited in the doc." });
    };
    const deletePost = async (postId: string, whole: boolean) => {
        const r = await run("delete", () => deleteOwnPost(doc.id, t.id, postId));
        setAsk(null);
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the post") });
        if (whole) return onView("doc");
        setMessage({ tone: "success", text: "Deleted from the doc." });
    };

    /* Log an answer */
    const logError = logTried && !logText.trim() ? "Write what they said." : null;
    const whenMs = Date.parse(`${logWhen}:00+08:00`);
    const whenError = logTried && (!Number.isFinite(whenMs) || whenMs > Date.now() + 60_000) ? "Pick a time that has passed." : null;
    const saveLog = async () => {
        setLogTried(true);
        if (!logText.trim() || !Number.isFinite(whenMs) || whenMs > Date.now() + 60_000) return;
        const body: LogAnswerIn = {
            via: logVia,
            text: logText.trim(),
            saidAt: new Date(whenMs).toISOString(),
            answer: logWord === "other" ? null : logWord,
        };
        const r = await run("log", () => logAnswer(doc.id, t.id, body));
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the doc") });
        setLogText("");
        setLogWord("other");
        setLogWhen(localNow());
        setLogTried(false);
        setMessage({ tone: "success", text: `Logged ${client === "The client" ? "the client's" : `${client}'s`} answer ${VIA_WORDS[logVia]}.` });
    };

    /* The opening comment */
    const opening = t.openedBy;
    const openingOurs = isOurOpening(t);
    const openingId = openingPostId(t);
    const clientPosted = t.posts.some((p) => p.author.side === "client");
    // A question is edited and removed in Questions, so the doc's list and its comment change together.
    const editInQuestions = question && Boolean(t.questionId);
    const openingDelete = openingOurs && !question && !clientPosted ? () => setAsk({ kind: "delete", postId: openingId, whole: true }) : undefined;

    /* The amber flag */
    const resolvePost = [...t.posts].reverse().find((p) => p.action === "resolve") ?? null;
    const lastWord = lastClientWord(t);
    const flagged = t.resolved && t.resolvedAgreed === false;
    const resolverName = resolvePost ? resolvePost.signedBy ?? authorWord(resolvePost.author) : t.resolvedBy ? authorWord(t.resolvedBy) : "Someone";
    const resolvedWhere = resolvePost?.where === "oura" ? "in Oura" : "in the doc";

    /* The foot */
    const others = needsUsThreads(threads).filter((x) => x.id !== t.id);
    const nextThread = others[0] ?? null;
    const stillNeedsUs = !t.resolved && t.state === "needs_us";
    const done: DrawerDone | null = said && !stillNeedsUs
        ? {
            said,
            left: others.length,
            group: "Needs us",
            next: nextThread ? { id: nextThread.id, title: threadTitle(nextThread, threads) } : null,
            audience: "staff",
            onNext: () => nextThread && onView(threadView(nextThread.id)),
            onDone: () => onView("doc"),
            lastLine: "Nothing else in this doc needs us.",
        }
        : null;
    const footMessage = done ? null : message ?? (said ? { tone: "success" as const, text: `${said}.` } : null);

    let confirm: DrawerConfirm | null = null;
    if (ask?.kind === "resolve") {
        confirm = { text: "The client has not agreed. Resolve anyway?", verb: "Resolve anyway", busy: busy === "resolve", onConfirm: () => void resolve(true), onCancel: () => setAsk(null) };
    } else if (ask?.kind === "delete") {
        confirm = {
            text: ask.whole ? "Delete this comment and its replies from the doc?" : "Delete this reply from the doc? The client can no longer read it.",
            verb: "Delete",
            busy: busy === "delete",
            onConfirm: () => void deletePost(ask.postId, ask.whole),
            onCancel: () => setAsk(null),
        };
    }

    const resolveButton = (primary: boolean) => (
        <PillButton variant={primary ? "primary" : "secondary"} busy={busy === "resolve"} disabled={busy !== null} onClick={() => void resolve(false)}>
            Resolve
        </PillButton>
    );
    let first: ReactNode;
    let second: ReactNode;
    if (t.resolved) {
        first = (
            <PillButton variant={flagged ? "primary" : "secondary"} icon={RotateCcwIcon} busy={busy === "reopen"} disabled={busy !== null} onClick={() => void reopen()}>
                Reopen
            </PillButton>
        );
        second =
            reopenWhy === null ? (
                back
            ) : (
                <PillButton disabled={busy !== null} onClick={() => setReopenWhy(null)}>
                    Cancel
                </PillButton>
            );
    } else if (reply.trim()) {
        first = (
            <PillButton variant="primary" busy={busy === "reply"} disabled={busy !== null} onClick={() => void sendReply()}>
                Send reply
            </PillButton>
        );
        second = resolveButton(false);
    } else {
        first = resolveButton(agreed);
        second = back;
    }

    const chips = t.code ? <FamilyChip family={codeFamily(t.code)}>{t.code}</FamilyChip> : undefined;
    const replies = t.posts.filter((p) => p.action === "reply" && (p.text.trim() || p.file));

    return (
        <>
            <DrawerHead title={threadTitle(t, threads)} status={threadStatus(t)} chips={chips} />

            <DrawerBlock>
                {t.quoted && <p className={cn(TYPE.tc, "m-0 mb-1 break-words text-gray-500")}>On &ldquo;{t.quoted}&rdquo;</p>}
                {editing === openingId ? (
                    <EditBox text={withoutCode(t.text, t.kind)} busy={busy === "edit"} onSave={(text) => void saveEdit(openingId, text)} onCancel={() => setEditing(null)} />
                ) : (
                    <>
                        <p className="m-0 whitespace-pre-line break-words text-gray-900">{withoutCode(t.text, t.kind)}</p>
                        <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>{joinDotted(authorWord(opening), "in the doc", docDate(t.openedAt))}</p>
                        {openingOurs && (
                            <OwnPostActions
                                disabled={busy !== null}
                                editLabel={editInQuestions ? "Edit in Questions" : "Edit"}
                                onEdit={() => (editInQuestions ? onView("questions") : setEditing(openingId))}
                                onDelete={openingDelete}
                            />
                        )}
                    </>
                )}
                {flagged && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5">
                        <Dot tone="amber" className="mt-[6px]" />
                        <p className={cn(TYPE.tc, "m-0 min-w-0 break-words text-amber-900")}>
                            <span className="font-medium">
                                Resolved by {resolverName} {resolvedWhere} without the client&rsquo;s agreement.
                            </span>{" "}
                            {lastWord ? <>Their last word: &ldquo;{trimText(lastWord.text, 120)}&rdquo;</> : "They had not answered."} Ask them to confirm, or reopen it.
                        </p>
                    </div>
                )}
            </DrawerBlock>

            {t.posts.length > 0 && (
                <DrawerBlock label={`Replies · ${replies.length}`}>
                    <div className="mt-2 space-y-2">
                        {t.posts.map((p) => {
                            if (p.action !== "reply") return <EventLine key={p.id} p={p} />;
                            if (!p.text.trim() && !p.file) return null;
                            const w = postWords(p);
                            const ours = isOurPost(p);
                            if (editing === p.id) {
                                return <EditBox key={p.id} text={p.text} busy={busy === "edit"} onSave={(text) => void saveEdit(p.id, text)} onCancel={() => setEditing(null)} />;
                            }
                            return (
                                <div key={p.id}>
                                    <DrawerQuote who={w.who} where={w.where} at={w.at}>
                                        {p.text}
                                        {p.file && (
                                            <a href={p.file.url} target="_blank" rel="noopener noreferrer" className="mt-1 block font-medium text-blue-600 hover:text-blue-700">
                                                {p.file.name}
                                                <span className="sr-only">, opens in a new tab</span>
                                            </a>
                                        )}
                                    </DrawerQuote>
                                    {ours && (
                                        <OwnPostActions
                                            disabled={busy !== null}
                                            onEdit={() => setEditing(p.id)}
                                            onDelete={() => setAsk({ kind: "delete", postId: p.id, whole: false })}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </DrawerBlock>
            )}

            {t.resolved && reopenWhy !== null && (
                <DrawerBlock label="Why reopen it" busy={busy === "reopen"}>
                    <textarea
                        className={cn(textareaClass(null), "mt-1.5")}
                        rows={2}
                        value={reopenWhy}
                        aria-label="Why reopen it"
                        placeholder="The client asked to change the hours again"
                        disabled={busy !== null}
                        onChange={(e) => {
                            setReopenWhy(e.target.value);
                            setMessage(null);
                        }}
                    />
                    <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>Posts in the thread with the reopen, {signed}.</p>
                </DrawerBlock>
            )}

            {!t.resolved && (
                <DrawerBlock label="Reply" busy={busy === "reply"}>
                    <textarea
                        className={cn(textareaClass(null), "mt-1.5")}
                        rows={3}
                        value={reply}
                        aria-label="Your reply"
                        placeholder={agreed ? "Thank them, or leave it and resolve" : "Write your reply"}
                        disabled={busy !== null}
                        onChange={(e) => {
                            setReply(e.target.value);
                            setSaid(null);
                        }}
                    />
                    <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                        {/* Oura's account from the doc's own share list: it differs per environment (#3974). */}
                        {ouraAccountOnDoc(doc) ? `Posts in the doc from ${ouraAccountOnDoc(doc)}, ${signed}.` : `Posts in the doc as Oura, ${signed}.`}
                    </p>
                </DrawerBlock>
            )}

            {canLog && (
                <DrawerFold id="staff-thread.log" label="Log an answer" note="Email, WhatsApp or a call" keepMounted>
                    <div className="space-y-3">
                        <div>
                            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>How they answered</p>
                            <Seg<Exclude<AnswerVia, "oura">> size="sm" ariaLabel="How they answered" value={logVia} onChange={setLogVia} items={VIA_ITEMS} />
                        </div>
                        <label className="block">
                            <span className={cn(TYPE.lab, "mb-1.5 block")}>Their words</span>
                            <textarea
                                className={textareaClass(logError)}
                                rows={3}
                                value={logText}
                                aria-invalid={Boolean(logError)}
                                disabled={busy !== null}
                                onChange={(e) => setLogText(e.target.value)}
                            />
                            {logError && <span role="alert" className={cn(TYPE.tc, "mt-1.5 block font-medium text-rose-600")}>{logError}</span>}
                        </label>
                        <label className="block">
                            <span className={cn(TYPE.lab, "mb-1.5 block")}>When they said it, Malaysian time</span>
                            <input
                                type="datetime-local"
                                className={inputClass(whenError)}
                                value={logWhen}
                                max={localNow()}
                                aria-invalid={Boolean(whenError)}
                                disabled={busy !== null}
                                onChange={(e) => setLogWhen(e.target.value)}
                            />
                            {whenError && <span role="alert" className={cn(TYPE.tc, "mt-1.5 block font-medium text-rose-600")}>{whenError}</span>}
                        </label>
                        <div>
                            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>Their answer</p>
                            <Seg<LogAnswerWord> size="sm" ariaLabel="Their answer" value={logWord} onChange={setLogWord} items={ANSWER_ITEMS} className="flex-wrap" />
                        </div>
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                            Posts in the thread as {client === "The client" ? "the client's" : `${client}'s`} words, {VIA_WORDS[logVia]}, logged by {me ?? "you"}.
                        </p>
                        <PillButton size="sm" variant="primary" busy={busy === "log"} disabled={busy !== null} onClick={() => void saveLog()}>
                            Log it
                        </PillButton>
                    </div>
                </DrawerFold>
            )}

            <DrawerFoot message={footMessage} done={done} confirm={confirm} first={done ? undefined : first} second={done ? undefined : second} />
        </>
    );
}
